import type { SimpleStyleT } from "tweenly"
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { t } from "i18next"
import React, { useCallback, useMemo } from "react"
import { getStyleFieldLabel } from "../../../../helpers/properties.helpers"
import useWrapSelectedObjects from "../../../../hooks/editor/useWrapSelectedObjects"
import useObject from "../../../../hooks/useObject"
import { useObjectActions } from "../../../../hooks/useObjectActions"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import {
    updateObjectsLockAspectRationPropertyAction,
    updateObjectsStylePropertyAction,
} from "../../../../store/slices/objects.slice"
import DimensionsPair from "../../../common/PropertyTools/DimensionsPair"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import { LockAspectRatio } from "./LockAspectRatio"

const MultiSize = () => {
    const selectedObjectIds = useAppSelector((state) => state.activeObject.selected)
    const lockAspectRation = useAppSelector((state) => state.editor.value.settings.lockAspectRatio)
    const { findObjectById } = useObject()
    const { updateObjectStyle } = useObjectActions()
    const { selectedObjects } = useWrapSelectedObjects()
    const dispatch = useAppDispatch()

    const getStylesValue = useCallback(
        (property: "width" | "height"): SimpleStyleT | undefined => {
            let oldValue: number | undefined = undefined
            let modifiedStyles = undefined
            selectedObjectIds.forEach((id: string) => {
                const stylesValues = findObjectById(id)?.styles.find(
                    (style) => style.property === property
                )
                if (oldValue === undefined) {
                    oldValue = stylesValues?.value
                    modifiedStyles = stylesValues
                } else {
                    if (oldValue !== stylesValues?.value) {
                        if (stylesValues) modifiedStyles = { ...stylesValues, value: undefined }
                    } else {
                        modifiedStyles = stylesValues
                    }
                }
            })
            return modifiedStyles
        },
        [findObjectById, selectedObjectIds]
    )

    const width: SimpleStyleT | undefined = useMemo(() => getStylesValue("width"), [getStylesValue])

    const height: SimpleStyleT | undefined = useMemo(
        () => getStylesValue("height"),
        [getStylesValue]
    )

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, property: string) => {
        if (!e.target.value) {
            return
        } else {
            if (lockAspectRation) {
                dispatch(
                    updateObjectsLockAspectRationPropertyAction({
                        objects: selectedObjects,
                        property: property,
                        value: e.target.value,
                    })
                )
            } else {
                dispatch(
                    updateObjectsStylePropertyAction({
                        objects: selectedObjects,
                        property: property,
                        value: e.target.value,
                    })
                )
            }
        }
    }

    /**
     * handler for set average value of width or height for selected objects
     * @param property name of property to update values of objects
     */
    const handleSetAverageValue = (property: "width" | "height") => {
        let valuesArray: number[] = []
        selectedObjectIds.map((x) =>
            valuesArray.push(
                Number(
                    findObjectById(x)?.styles.find((style) => style.property === property)?.value
                )
            )
        )
        const averageWidth: number = valuesArray.reduce((a, b) => a + b) / valuesArray.length
        selectedObjectIds.forEach((id) => {
            let object = findObjectById(id)
            if (object) updateObjectStyle(object, property, Number(averageWidth.toFixed(2)))
        })
    }

    // values that differ across the selection show as "Mixed", like Figma
    const averageButton = (property: "width" | "height") => (
        <Tooltip
            title={t(
                property === "width"
                    ? "editor:size&positionSettings.multiSize.AverageWidthTooltip"
                    : "editor:size&positionSettings.multiSize.AverageHeightTooltip"
            )}
        >
            <IconButton size="small" edge="end" onClick={() => handleSetAverageValue(property)}>
                <AutoFixHighRoundedIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    )

    return (
        <PropertyRow gutter={<LockAspectRatio size="small" />}>
            <DimensionsPair
                locked={lockAspectRation}
                width={
                    width && (
                        <PrefixedInput
                            name="width"
                            value={width.value}
                            placeholder={width.value === undefined ? "Mixed" : undefined}
                            label={getStyleFieldLabel(width)}
                            tooltip="Width"
                            handleOnChange={(e) => handleOnChange(e, "width")}
                            min={0}
                            endAdornment={averageButton("width")}
                        />
                    )
                }
                height={
                    height && (
                        <PrefixedInput
                            name="height"
                            value={height.value}
                            placeholder={height.value === undefined ? "Mixed" : undefined}
                            label={getStyleFieldLabel(height)}
                            tooltip="Height"
                            handleOnChange={(e) => handleOnChange(e, "height")}
                            min={0}
                            endAdornment={averageButton("height")}
                        />
                    )
                }
            />
        </PropertyRow>
    )
}

export default MultiSize
